/**
 * furniture_place.ts — client-side helpers for the FURNISHING wave, shared by
 * DecorationModule (render fit + pickup prompt), AtlasCraftingModule (the
 * Place action), and BuildingInteriorModule (versona-home furnishing):
 *
 *  · fit-scale a generated furniture GLB into its catalog dims (the
 *    multi_image_to_3d models come out at arbitrary scale and pivot);
 *  · structure-local ↔ world transforms (matching DecorationModule's
 *    `world = structure ∘ local` composition);
 *  · admin approximation for the UI (the server re-checks on every reducer).
 */
import * as THREE from 'three/webgpu';
import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';
import { FURNITURE, type FurnitureSpec } from './furniture_catalog.js';

/** Render boost over the nominal catalog dims: the conservative fit-inside
 *  scale read visibly small in real rooms (field feedback 2026-08-14). */
export const FURNITURE_RENDER_SCALE = 1.5;

/** Uniform scale fitting a GLB bbox INSIDE the spec dims (×render boost) +
 *  the bottom-seat lift that puts the model's measured base at local y=0. */
export function furnitureFit(spec: FurnitureSpec, bb: THREE.Box3): { scale: number; yOff: number } {
  const gw = Math.max(0.05, bb.max.x - bb.min.x);
  const gh = Math.max(0.05, bb.max.y - bb.min.y);
  const gd = Math.max(0.05, bb.max.z - bb.min.z);
  const scale = Math.min(spec.w / gw, spec.h / gh, spec.d / gd) * FURNITURE_RENDER_SCALE;
  return { scale, yOff: -bb.min.y * scale };
}

/** The FurnitureSpec for a decor prefabId ('furn_<key>'), or null. */
export function furnitureSpecOf(prefabId: string): FurnitureSpec | null {
  return prefabId.startsWith('furn_') ? FURNITURE[prefabId.slice(5)] ?? null : null;
}

/** World → structure-local (inverse of DecorationModule's composition). */
export function worldToLocal(s: { posX: number; posZ: number; yaw: number }, wx: number, wz: number): { lx: number; lz: number } {
  const dx = wx - s.posX, dz = wz - s.posZ;
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { lx: c * dx - sn * dz, lz: sn * dx + c * dz };
}

/** Structure-local → world (forward composition, positions only). */
export function localToWorld(s: { posX: number; posZ: number; yaw: number }, lx: number, lz: number): { x: number; z: number } {
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { x: s.posX + c * lx + sn * lz, z: s.posZ - sn * lx + c * lz };
}

/** UI-side admin check (owner or an owner/admin permission row) — the server
 *  is the authority; this only gates what the UI offers. */
export function canAdminStructureUi(conn: DbConnection, structureId: bigint, me: Identity): boolean {
  const s = conn.db.structure.structureId.find(structureId);
  if (!s) return false;
  if (s.owner.isEqual(me)) return true;
  for (const p of conn.db.structurePermission.structurePermissionByStructure.filter(structureId)) {
    if (p.subject.isEqual(me) && (p.level === 'owner' || p.level === 'admin')) return true;
  }
  return false;
}

/** Nearest structure the sender may furnish, within reach of (x, z). */
export function furnishableStructureNear(conn: DbConnection, me: Identity, worldId: bigint, x: number, z: number, reachM = 40): { structureId: bigint; posX: number; posZ: number; yaw: number } | null {
  let best: { structureId: bigint; posX: number; posZ: number; yaw: number } | null = null;
  let bestD = reachM;
  for (const s of conn.db.structure.iter()) {
    if (s.worldId !== worldId) continue;
    const d = Math.hypot(s.posX - x, s.posZ - z);
    if (d < bestD && canAdminStructureUi(conn, s.structureId, me)) {
      bestD = d;
      best = { structureId: s.structureId, posX: s.posX, posZ: s.posZ, yaw: s.yaw };
    }
  }
  return best;
}
